/**
 * productBundles — "Materiais inclusos" num serviço (kit / lista de materiais).
 *
 * ARQUITETURA (a mesma lição das variações, armadilha #30): o material NÃO é um
 * campo do serviço nem uma linha nova de outro tipo — é uma LIGAÇÃO entre duas
 * linhas de `products`. Quando o serviço entra no orçamento, cada material vira
 * um `quote_items` PRÓPRIO, com `product_id` apontando para o produto real.
 *
 * Por que materializar como itens em vez de guardar o kit "fechado":
 *  - `toDeductionLines` (src/lib/stock.ts) consolida a baixa por `product_id`.
 *    Como cada material tem id próprio, a baixa de estoque sai de graça e
 *    corretamente — inclusive somando o mesmo material que apareça em dois
 *    serviços do mesmo orçamento.
 *  - o cliente vê no PDF o que está incluso, e o prestador pode ajustar
 *    quantidade ou remover uma linha sem editar o cadastro.
 *  - `quote_items.unit_cost` guarda o snapshot do custo de CADA material, que
 *    é a base de margem/CMV dos grupos 5 e 6.
 *
 * ─── ONDE O KIT VALE (atualizado no grupo 10) ──────────────────────────────
 *
 * Durante os grupos 2–7 o kit valia SÓ para orçamento; a assimetria está
 * resolvida. O que mudou em cada frente, e o que continua fora:
 *
 *  • **Orçamento** — expande desde o grupo 2 (`bundleLinesForQuote`).
 *
 *  • **Ordem de serviço — EXPANDE (grupo 10).** O que travava não era o custo
 *    (a coluna existe desde o grupo 5), era a DUPLA MATERIALIZAÇÃO: uma OS
 *    nascida de orçamento já herda os materiais como itens, então expandir de
 *    novo duplicaria a linha e a baixa de estoque. A trava saiu com dois
 *    mecanismos: a linha materializada guarda a ORIGEM
 *    (`service_order_items.bundle_parent_product_id`) e a expansão recusa um
 *    material cujo produto já está no documento (`bundleLinesForServiceOrder`).
 *    Com isso, expandir duas vezes é operação idempotente e a OS herdada não
 *    duplica nada — o seed a partir do orçamento nem passa pela expansão.
 *
 *  • **PDV — expande, mas SÓ com o operador vendo (grupo 10).** A objeção
 *    original continua de pé: o balcão baixa estoque automaticamente, e o
 *    perigo era o serviço subtrair materiais que ninguém escolheu nem viu. A
 *    saída não foi expandir escondido, foi TORNAR VISÍVEL — o carrinho mostra
 *    cada material como linha própria, com a projeção de saldo, antes de
 *    finalizar; e o operador pode remover. O que ele confirma é exatamente o
 *    que sai do estoque, que era o requisito real.
 *
 * Este arquivo é PURO (sem I/O) de propósito — é o que os testes cobrem.
 */
import { productUnitCost } from '@/lib/cost';
import { isVariant, variantDisplayName } from '@/lib/variants';

/** Uma linha de `product_bundle_items` como ela vem do banco. */
export interface BundleItemRow {
  id?: string;
  company_id?: string | null;
  parent_product_id: string;
  child_product_id: string;
  quantity: number | string;
}

/** O mínimo que estes helpers precisam de um produto do catálogo. */
export interface BundleAwareProduct {
  id: string;
  name?: string | null;
  description?: string | null;
  price?: number | null;
  cost?: number | string | null;
  type?: string | null;
  unit?: string | null;
  image_url?: string | null;
  barcode?: string | null;
  parent_product_id?: string | null;
  variant_name?: string | null;
}

/** Um material já resolvido contra o catálogo (o que a UI e o orçamento usam). */
export interface ResolvedBundleItem {
  /** Id da linha de ligação (ausente enquanto o vínculo é só um rascunho). */
  id?: string;
  quantity: number;
  product: BundleAwareProduct;
  /** Nome como o usuário lê: já resolve "Camiseta — P" quando é variação. */
  displayName: string;
}

/** Quantidade de um vínculo, tolerante ao `numeric` que chega como string. */
export function bundleQuantity(raw: number | string | null | undefined): number {
  const n = Number(raw ?? 0);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return n;
}

/** Agrupa as ligações por serviço pai. */
export function groupBundleItems(rows: BundleItemRow[]): Map<string, BundleItemRow[]> {
  const byParent = new Map<string, BundleItemRow[]>();
  for (const row of rows ?? []) {
    if (!row?.parent_product_id || !row?.child_product_id) continue;
    const key = String(row.parent_product_id);
    const list = byParent.get(key);
    if (list) list.push(row);
    else byParent.set(key, [row]);
  }
  return byParent;
}

/**
 * Resolve as ligações de UM serviço contra o catálogo.
 *
 * Um material cujo produto não está na lista é DESCARTADO — não vira uma linha
 * fantasma. Isso acontece legitimamente quando o catálogo em mãos é o cache
 * offline e ele ainda não conhece um produto criado em outro dispositivo.
 */
export function resolveBundleItems(
  rows: BundleItemRow[],
  products: BundleAwareProduct[],
): ResolvedBundleItem[] {
  const byId = new Map<string, BundleAwareProduct>((products ?? []).map((p) => [String(p.id), p]));

  return (rows ?? [])
    .map((row) => {
      const product = byId.get(String(row.child_product_id));
      if (!product) return null;
      const parentOfVariant = isVariant(product)
        ? byId.get(String(product.parent_product_id))
        : undefined;
      return {
        id: row.id,
        quantity: bundleQuantity(row.quantity),
        product,
        displayName: isVariant(product)
          ? variantDisplayName(parentOfVariant?.name ?? product.name, product.variant_name)
          : String(product.name ?? ''),
      } as ResolvedBundleItem;
    })
    .filter((item): item is ResolvedBundleItem => item != null)
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'pt-BR'));
}

/**
 * Um material pode ser vinculado a um kit?
 *
 * O banco impõe as mesmas regras (trigger `product_bundle_guard`), mas a UI
 * precisa saber ANTES para não oferecer o item e depois falhar com erro cru.
 *
 * `parentsWithVariants` vem de `parentIdsWithVariants` (src/lib/variants.ts):
 * um produto que TEM variações não é vendável — quem entra no orçamento e no
 * estoque é a variação, então é ela que pode ser material.
 */
export function canBeBundleMaterial(
  product: BundleAwareProduct,
  opts: {
    parentId: string;
    parentsWithVariants: Set<string>;
    /** Ids que já são kits (não podem virar material — um nível só). */
    kitParentIds?: Set<string>;
    /** Ids que já são material de algum kit (não podem virar kit). */
    materialIds?: Set<string>;
  },
): boolean {
  const id = String(product?.id ?? '');
  if (!id) return false;
  if (id === String(opts.parentId)) return false;
  if (opts.parentsWithVariants.has(id)) return false;
  if (opts.kitParentIds?.has(id)) return false;
  return true;
}

/**
 * O serviço em si pode receber materiais? (o outro lado do "nível único")
 * Um item que já é material de outro kit não pode virar kit.
 */
export function canHaveBundleMaterials(productId: string, materialIds: Set<string>): boolean {
  return !materialIds.has(String(productId));
}

/** Um item de orçamento gerado a partir de um material do kit. */
export interface BundleQuoteLine {
  name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount: number;
  source: 'catalog';
  itemType: 'service' | 'product';
  unit: string;
  image_url?: string;
  product_id: string;
  barcode?: string;
  /** Snapshot do custo, igual ao de qualquer item vindo do catálogo. */
  unit_cost: number | null;
  /** Id do serviço que trouxe este material — só para a UI explicar a origem. */
  bundleParentId: string;
}

/**
 * Converte os materiais de um serviço nas linhas de orçamento correspondentes.
 *
 * A quantidade é `quantidade do kit × quantidade do serviço`: pedir 3 instalações
 * que consomem 2 buchas cada precisa baixar 6 buchas do estoque.
 */
export function bundleLinesForQuote(
  items: ResolvedBundleItem[],
  opts: { parentId: string; parentQuantity?: number },
): BundleQuoteLine[] {
  const parentQty = Number(opts.parentQuantity ?? 1);
  const multiplier = Number.isFinite(parentQty) && parentQty > 0 ? parentQty : 1;

  return (items ?? []).map((item) => ({
    name: item.displayName,
    description: item.product.description || '',
    quantity: item.quantity * multiplier,
    unit_price: Number(item.product.price ?? 0),
    discount: 0,
    source: 'catalog' as const,
    itemType: item.product.type === 'product' ? ('product' as const) : ('service' as const),
    unit: item.product.unit || 'un',
    image_url: item.product.image_url || undefined,
    product_id: String(item.product.id),
    barcode: item.product.barcode || undefined,
    // Mesmo momento e mesma regra do item principal: o custo é lido do catálogo
    // UMA vez, agora, e congelado (ver quoteItems.ts / armadilha #61).
    unit_cost: productUnitCost(item.product),
    bundleParentId: String(opts.parentId),
  }));
}

/** Uma linha de OS gerada a partir de um material do kit. */
export interface BundleServiceOrderLine {
  name: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: 'value';
  item_type: 'service' | 'product';
  unit: string;
  image_url: string | null;
  product_id: string;
  barcode: string | null;
  /** Snapshot do custo, igual ao de qualquer item vindo do catálogo (#61/#77). */
  unit_cost: number | null;
  /** A ORIGEM, que é o que torna a expansão idempotente. */
  bundle_parent_product_id: string;
}

/**
 * Converte os materiais de um serviço nas linhas de ORDEM DE SERVIÇO.
 *
 * ─── A REGRA DE IDEMPOTÊNCIA, que é o coração do B1 ────────────────────────
 *
 * `existingProductIds` são os produtos que JÁ estão na OS. Um material cujo
 * produto já está lá NÃO entra de novo. É uma regra só, e ela cobre os três
 * casos que preocupavam:
 *
 *  1. **expandir o mesmo kit duas vezes** — a segunda não acrescenta nada;
 *  2. **OS herdada de um orçamento** — os materiais vieram do orçamento como
 *     itens comuns (sem marca de origem, porque `quote_items` não tem a
 *     coluna); mesmo assim são reconhecidos, porque a regra olha o
 *     `product_id`, não a marca;
 *  3. **material que o usuário adicionou à mão** — a quantidade que ele
 *     digitou vale mais que a do kit, mesmo comportamento do orçamento.
 *
 * O preço dessa regra é conhecido e aceito: se o usuário já tinha 10 metros de
 * cabo na OS e adiciona um kit que inclui 2, os 2 do kit NÃO são somados. Errar
 * para MENOS é recuperável (a linha está na tela, dá para editar a quantidade);
 * errar para mais cobraria o cliente em dobro e baixaria estoque em dobro, que
 * é justamente o que manteve esta feature fora por três grupos.
 */
export function bundleLinesForServiceOrder(
  items: ResolvedBundleItem[],
  opts: {
    parentId: string;
    parentQuantity?: number;
    existingProductIds?: ReadonlySet<string>;
  },
): BundleServiceOrderLine[] {
  const existing = opts.existingProductIds ?? new Set<string>();

  return bundleLinesForQuote(items, {
    parentId: opts.parentId,
    parentQuantity: opts.parentQuantity,
  })
    .filter((line) => !existing.has(String(line.product_id)))
    .map((line) => ({
      name: line.name,
      description: line.description,
      quantity: line.quantity,
      unit_price: line.unit_price,
      discount: line.discount,
      discount_type: 'value' as const,
      item_type: line.itemType,
      unit: line.unit,
      image_url: line.image_url ?? null,
      product_id: line.product_id,
      barcode: line.barcode ?? null,
      unit_cost: line.unit_cost,
      bundle_parent_product_id: line.bundleParentId,
    }));
}

/** Soma do custo dos materiais (para o resumo do bloco no formulário). */
export function bundleTotalCost(items: ResolvedBundleItem[]): { total: number; missingCost: number } {
  let total = 0;
  let missingCost = 0;
  for (const item of items ?? []) {
    const cost = productUnitCost(item.product);
    // Ausência NUNCA vira zero (armadilha #61): é contada à parte para a tela
    // poder dizer que o total está incompleto, em vez de mentir um número menor.
    if (cost == null) missingCost += 1;
    else total += cost * item.quantity;
  }
  return { total, missingCost };
}

/** Soma do preço de venda dos materiais (o que o cliente pagaria à parte). */
export function bundleTotalPrice(items: ResolvedBundleItem[]): number {
  return (items ?? []).reduce((sum, item) => sum + Number(item.product.price ?? 0) * item.quantity, 0);
}
